/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

import { useMemo } from "react";

import type {
  DesignTokens,
  SemanticTokenScale,
} from "@/components/ui/foundation/design-tokens";
import { designTokens } from "@/components/ui/foundation/design-tokens";

import type {
  SemanticIntent,
  SemanticSurfaceColors,
  SemanticSurfaceShadeConfig,
  SurfaceContext,
} from "./types";

type ColorScaleKey = Exclude<keyof DesignTokens["colors"], "semantic">;
type SemanticScaleKey = keyof DesignTokens["colors"]["semantic"];
type SpacingKey = keyof typeof designTokens.spacing;
type BreakpointKey = keyof DesignTokens["breakpoints"];
type FontSizeKey = keyof DesignTokens["typography"]["fontSize"];
type FontWeightKey = keyof DesignTokens["typography"]["fontWeight"];
type FontFamilyKey = keyof DesignTokens["typography"]["fontFamily"];
type TokenShade = keyof SemanticTokenScale;

// Re-export shared types
export type {
  SemanticIntent,
  SemanticSurfaceColors,
  SemanticSurfaceShadeConfig,
  SurfaceContext,
} from "./types";

type SemanticInteractionShadeConfig = {
  hoverBackground: TokenShade;
  hoverText: TokenShade;
  hoverBorder: TokenShade;
  focusRing: TokenShade;
};

export interface SemanticInteractionColors {
  hoverBackground: string;
  hoverText: string;
  hoverBorder: string;
  focusRing: string;
}

type SpacingProperty = "p" | "m" | "gap" | "space";
type SpacingAxis = "x" | "y" | "t" | "r" | "b" | "l";

export interface UseDesignTokensResult {
  tokens: DesignTokens;
  getSpacingValue: (key: SpacingKey) => string;
  getBreakpointValue: (key: BreakpointKey) => string;
  getColorValue: (
    scale: ColorScaleKey,
    shade: keyof SemanticTokenScale,
  ) => string;
  getSemanticColorValue: (
    scale: SemanticScaleKey,
    shade?: keyof SemanticTokenScale,
  ) => string;
  getSemanticSurfaceColors: (
    intent: SemanticIntent,
    context?: SurfaceContext,
    overrides?: Partial<SemanticSurfaceShadeConfig>,
  ) => SemanticSurfaceColors;
  getSemanticInteractionColors: (
    intent: SemanticIntent,
    overrides?: Partial<SemanticInteractionShadeConfig>,
  ) => SemanticInteractionColors;
  buildSpacingClass: (
    property: SpacingProperty,
    key: SpacingKey,
    axis?: SpacingAxis,
  ) => string;
  getFontSize: (key: FontSizeKey) => [string, { lineHeight: string }];
  getFontWeight: (key: FontWeightKey) => string;
  getFontFamily: (key: FontFamilyKey) => string[];
  buildTypographyClasses: (options: {
    size?: FontSizeKey;
    weight?: FontWeightKey;
    family?: FontFamilyKey;
  }) => string;
}

export function useDesignTokens(): UseDesignTokensResult {
  return useMemo(() => {
    const getSpacingValue = (key: SpacingKey): string =>
      designTokens.spacing[key] ?? (designTokens.spacing[0] as string);

    const getBreakpointValue = (key: BreakpointKey): string =>
      designTokens.breakpoints[key] as string;

    const getColorValue = (
      scale: ColorScaleKey,
      shade: keyof SemanticTokenScale,
    ): string => {
      const colorScale = designTokens.colors[scale];
      return String(colorScale[shade] ?? colorScale[500]);
    };

    const getSemanticColorValue = (
      scale: SemanticScaleKey,
      shade: keyof SemanticTokenScale = 500,
    ): string => {
      const semanticScale = designTokens.colors.semantic[scale];
      return String(semanticScale[shade] ?? semanticScale[500]);
    };

    const contextualSemanticSurfaceShades: Record<
      SurfaceContext,
      Record<SemanticIntent, SemanticSurfaceShadeConfig>
    > = {
      "on-light": {
        success: {
          text: 700,
          strongText: 800,
          mutedText: 600,
          background: 50,
          subtleBackground: 100,
          border: 200,
          icon: 500,
        },
        warning: {
          text: 700,
          strongText: 800,
          mutedText: 600,
          background: 50,
          subtleBackground: 100,
          border: 200,
          icon: 500,
        },
        error: {
          text: 700,
          strongText: 800,
          mutedText: 600,
          background: 50,
          subtleBackground: 100,
          border: 200,
          icon: 500,
        },
        info: {
          text: 700,
          strongText: 800,
          mutedText: 600,
          background: 50,
          subtleBackground: 100,
          border: 200,
          icon: 500,
        },
        primary: {
          text: 800,
          strongText: 900,
          mutedText: 700,
          background: 0,
          subtleBackground: 50,
          border: 200,
          icon: 600,
        },
        neutral: {
          text: 800,
          strongText: 900,
          mutedText: 600,
          background: 0,
          subtleBackground: 50,
          border: 200,
          icon: 500,
        },
      },
      "on-dark": {
        success: {
          text: 100,
          strongText: 50,
          mutedText: 200,
          background: 900,
          subtleBackground: 800,
          border: 700,
          icon: 400,
        },
        warning: {
          text: 100,
          strongText: 50,
          mutedText: 200,
          background: 900,
          subtleBackground: 800,
          border: 700,
          icon: 400,
        },
        error: {
          text: 100,
          strongText: 50,
          mutedText: 200,
          background: 900,
          subtleBackground: 800,
          border: 700,
          icon: 400,
        },
        info: {
          text: 100,
          strongText: 50,
          mutedText: 200,
          background: 900,
          subtleBackground: 800,
          border: 700,
          icon: 400,
        },
        primary: {
          text: 100,
          strongText: 50,
          mutedText: 200,
          background: 800,
          subtleBackground: 700,
          border: 600,
          icon: 400,
        },
        neutral: {
          text: 100,
          strongText: 50,
          mutedText: 200,
          background: 800,
          subtleBackground: 700,
          border: 600,
          icon: 400,
        },
      },
      "on-brand": {
        success: {
          text: 50,
          strongText: 0,
          mutedText: 100,
          background: 600,
          subtleBackground: 500,
          border: 400,
          icon: 200,
        },
        warning: {
          text: 50,
          strongText: 0,
          mutedText: 100,
          background: 600,
          subtleBackground: 500,
          border: 400,
          icon: 200,
        },
        error: {
          text: 50,
          strongText: 0,
          mutedText: 100,
          background: 600,
          subtleBackground: 500,
          border: 400,
          icon: 200,
        },
        info: {
          text: 50,
          strongText: 0,
          mutedText: 100,
          background: 600,
          subtleBackground: 500,
          border: 400,
          icon: 200,
        },
        primary: {
          text: 50,
          strongText: 0,
          mutedText: 100,
          background: 600,
          subtleBackground: 500,
          border: 400,
          icon: 200,
        },
        neutral: {
          text: 50,
          strongText: 0,
          mutedText: 100,
          background: 700,
          subtleBackground: 600,
          border: 500,
          icon: 300,
        },
      },
    } as const;

    const getIntentColorValue = (
      intent: SemanticIntent,
      shade: TokenShade,
    ): string => {
      if (intent === "primary" || intent === "neutral") {
        return getColorValue(intent, shade);
      }

      return getSemanticColorValue(intent, shade);
    };

    const getSemanticSurfaceColors = (
      intent: SemanticIntent,
      context: SurfaceContext = "on-light",
      overrides: Partial<SemanticSurfaceShadeConfig> = {},
    ): SemanticSurfaceColors => {
      // Validate intent and fallback to neutral for invalid intents
      const validIntent: SemanticIntent =
        intent &&
        typeof intent === "string" &&
        (intent === "primary" ||
          intent === "neutral" ||
          intent === "success" ||
          intent === "warning" ||
          intent === "error" ||
          intent === "info")
          ? intent
          : "neutral";

      const shades = {
        ...contextualSemanticSurfaceShades[context][validIntent],
        ...overrides,
      };

      return {
        text: getIntentColorValue(validIntent, shades.text),
        textStrong: getIntentColorValue(validIntent, shades.strongText),
        textMuted: getIntentColorValue(validIntent, shades.mutedText),
        background: getIntentColorValue(validIntent, shades.background),
        backgroundMuted: getIntentColorValue(
          validIntent,
          shades.subtleBackground,
        ),
        border: getIntentColorValue(validIntent, shades.border),
        icon: getIntentColorValue(validIntent, shades.icon),
      };
    };

    const defaultSemanticInteractionShades: Record<
      SemanticIntent,
      SemanticInteractionShadeConfig
    > = {
      success: {
        hoverBackground: 100,
        hoverText: 700,
        hoverBorder: 200,
        focusRing: 300,
      },
      warning: {
        hoverBackground: 100,
        hoverText: 700,
        hoverBorder: 200,
        focusRing: 300,
      },
      error: {
        hoverBackground: 100,
        hoverText: 700,
        hoverBorder: 200,
        focusRing: 300,
      },
      info: {
        hoverBackground: 100,
        hoverText: 700,
        hoverBorder: 200,
        focusRing: 300,
      },
      primary: {
        hoverBackground: 100,
        hoverText: 800,
        hoverBorder: 200,
        focusRing: 300,
      },
      neutral: {
        hoverBackground: 100,
        hoverText: 700,
        hoverBorder: 200,
        focusRing: 300,
      },
    } as const;

    const getSemanticInteractionColors = (
      intent: SemanticIntent,
      overrides: Partial<SemanticInteractionShadeConfig> = {},
    ): SemanticInteractionColors => {
      const shades = {
        ...defaultSemanticInteractionShades[intent],
        ...overrides,
      };

      return {
        hoverBackground: getIntentColorValue(intent, shades.hoverBackground),
        hoverText: getIntentColorValue(intent, shades.hoverText),
        hoverBorder: getIntentColorValue(intent, shades.hoverBorder),
        focusRing: getIntentColorValue(intent, shades.focusRing),
      };
    };

    const buildSpacingClass = (
      property: SpacingProperty,
      key: SpacingKey,
      axis?: SpacingAxis,
    ): string => {
      if (property === "space") {
        const axisSegment = axis ? `-${axis}` : "-y";
        return `${property}${axisSegment}-${key}`;
      }

      if (property === "gap") {
        const axisSegment = axis ? `-${axis}` : "";
        return `${property}${axisSegment}-${key}`;
      }

      const axisSuffix = axis ?? "";
      return `${property}${axisSuffix}-${key}`;
    };

    const getFontSize = (
      key: FontSizeKey,
    ): [string, { lineHeight: string }] => {
      return designTokens.typography.fontSize[key] as [
        string,
        { lineHeight: string },
      ];
    };

    const getFontWeight = (key: FontWeightKey): string => {
      return designTokens.typography.fontWeight[key] as string;
    };

    const getFontFamily = (key: FontFamilyKey): string[] => {
      const fontFamily = designTokens.typography.fontFamily[key];
      return fontFamily ?? designTokens.typography.fontFamily.sans;
    };

    const buildTypographyClasses = (options: {
      size?: FontSizeKey;
      weight?: FontWeightKey;
      family?: FontFamilyKey;
    }): string => {
      const classes: string[] = [];

      if (options.size) {
        classes.push(`text-${options.size}`);
      }

      if (options.weight) {
        classes.push(`font-${options.weight}`);
      }

      if (options.family) {
        classes.push(`font-${options.family}`);
      }

      return classes.join(" ");
    };

    return {
      tokens: designTokens,
      getSpacingValue,
      getBreakpointValue,
      getColorValue,
      getSemanticColorValue,
      getSemanticSurfaceColors,
      getSemanticInteractionColors,
      buildSpacingClass,
      getFontSize,
      getFontWeight,
      getFontFamily,
      buildTypographyClasses,
    };
  }, []);
}
